import { applyEasing, EASING_PRESETS } from '../easing.js'

// Fixed curve for all styles - not user-configurable like clip transitions' curve editor.
const EASE_IN_OUT_POINTS = EASING_PRESETS.find(p => p.key === 'ease-in-out').points

const OPPOSITE_DIRECTION = { left: 'right', right: 'left', up: 'down', down: 'up' }

export const mirrorDirection = (direction) => OPPOSITE_DIRECTION[direction] || direction

const DEFAULT_ANIMATION = { styles: [], direction: 'left', durationMs: 0 }

// continuity: A->B then B->C, same real motion so direction flips. mirror: A->B then B->A,
// direction stays the same. different: use the independently stored animationOut.
export const resolveAnimationOut = (overlay) => {
    const linkMode = overlay.animationLinkMode || 'different'
    const animIn = overlay.animationIn || DEFAULT_ANIMATION
    if (linkMode === 'continuity') return { ...animIn, direction: mirrorDirection(animIn.direction) }
    if (linkMode === 'mirror') return animIn
    return overlay.animationOut || DEFAULT_ANIMATION
}

const IDENTITY = { opacity: 1, offsetXPct: 0, offsetYPct: 0, scale: 1, rotationDeg: 0, blurPx: 0 }

// Applied via a canvas blur filter (setObjectBlur), not a Fabric image filter - those only work
// on fabric.Image, not the text/group/shape objects animated here.
const MAX_BLUR_PX = 16

// Penner "ease out back" overshoot - bounce's whole character is this, so it skips the shared
// ease-in-out curve.
const backEaseOut = (t) => {
    const c1 = 1.70158
    const c3 = c1 + 1
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}

const DIRECTION_AXIS = { left: 'x', right: 'x', up: 'y', down: 'y' }
const DIRECTION_SIGN = { left: -1, right: 1, up: -1, down: 1 }

// Each style touches only its own property (fade=opacity, slide=position...) so combining
// several composes predictably. revealAmount: 0 = hidden, 1 = settled.
const STYLE_CONTRIBUTION = {
    fade: (direction, revealAmount) => ({ ...IDENTITY, opacity: revealAmount }),

    slide: (direction, revealAmount) => {
        const axis = DIRECTION_AXIS[direction] || 'x'
        const sign = DIRECTION_SIGN[direction] || -1
        const offset = (1 - revealAmount) * sign
        return { ...IDENTITY, offsetXPct: axis === 'x' ? offset : 0, offsetYPct: axis === 'y' ? offset : 0 }
    },

    zoom: (direction, revealAmount) => ({ ...IDENTITY, scale: 0.3 + 0.7 * revealAmount }),

    bounce: (direction, revealAmount) => ({ ...IDENTITY, scale: Math.max(0, revealAmount >= 1 ? 1 : backEaseOut(revealAmount)) }),

    rotate: (direction, revealAmount) => {
        const sign = (direction === 'right' || direction === 'down') ? 1 : -1
        return { ...IDENTITY, rotationDeg: (1 - revealAmount) * 90 * sign }
    },

    blur: (direction, revealAmount) => ({ ...IDENTITY, blurPx: (1 - revealAmount) * MAX_BLUR_PX })
}

// opacity/scale multiply (can only dim/shrink further), offset/rotation add.
const applyStyles = (styles, direction, revealAmount) => {
    const active = (styles || []).filter(style => STYLE_CONTRIBUTION[style])
    if (!active.length) return IDENTITY
    return active.reduce((acc, style) => {
        const c = STYLE_CONTRIBUTION[style](direction, revealAmount)
        return {
            opacity: acc.opacity * c.opacity,
            offsetXPct: acc.offsetXPct + c.offsetXPct,
            offsetYPct: acc.offsetYPct + c.offsetYPct,
            scale: acc.scale * c.scale,
            rotationDeg: acc.rotationDeg + c.rotationDeg,
            // Strongest wins, not additive - "twice as blurry" isn't meaningful.
            blurPx: Math.max(acc.blurPx, c.blurPx)
        }
    }, IDENTITY)
}

// Shared by the live canvas (OverlayCanvas.vue) and the offline export renderer.
export const computeOverlayAnimationTransform = (overlay, currentMs) => {
    const t = currentMs - overlay.startMs
    const totalMs = overlay.endMs - overlay.startMs
    const animIn = overlay.animationIn || DEFAULT_ANIMATION
    const animOut = resolveAnimationOut(overlay)
    const inDur = Math.max(0, animIn.durationMs || 0)
    const outDur = Math.max(0, animOut.durationMs || 0)
    const outStart = totalMs - outDur

    if (animIn.styles && animIn.styles.length && inDur > 0 && t < inDur) {
        const revealAmount = applyEasing(Math.max(0, Math.min(1, t / inDur)), EASE_IN_OUT_POINTS)
        return applyStyles(animIn.styles, animIn.direction, revealAmount)
    }
    if (animOut.styles && animOut.styles.length && outDur > 0 && t > outStart) {
        const revealAmount = 1 - applyEasing(Math.max(0, Math.min(1, (t - outStart) / outDur)), EASE_IN_OUT_POINTS)
        return applyStyles(animOut.styles, animOut.direction, revealAmount)
    }
    return IDENTITY
}
